export enum RoleCode {
  ADMIN = "ADMIN",
  OPERATOR = "OPERATOR",
  DEVELOPER = "DEVELOPER",
  MINOR_REVIEWER = "MINOR_REVIEWER",
  MAJOR_REVIEWER = "MAJOR_REVIEWER",
  /**
   * Read-only planogram viewer for merchant office users (space planners, commercial). Sees only
   * Space Planning's Active and Historical lists, restricted to delivered store POGs, and the
   * read-only store POG viewer. Holds `planogram:view` but not `merchflow:read`.
   */
  STORE_VIEWER = "STORE_VIEWER",
}

export enum PermissionCode {
  // Stores
  STORES_READ = "stores:read",
  STORES_CREATE = "stores:create",
  STORES_UPDATE = "stores:update",

  // Categories
  CATEGORIES_READ = "categories:read",
  CATEGORIES_CREATE = "categories:create",
  CATEGORIES_UPDATE = "categories:update",

  // Merchflow
  MERCHFLOW_READ = "merchflow:read",
  MERCHFLOW_CREATE = "merchflow:create",
  MERCHFLOW_UPDATE = "merchflow:update",
  MERCHFLOW_DELETE = "merchflow:delete",

  // Store POG
  STORE_POG_DELETE = "store-pog:delete",

  // Planogram viewing. Without MERCHFLOW_READ it narrows to delivered store POGs only.
  PLANOGRAM_VIEW = "planogram:view",

  // Report
  REPORT_READ = "report:read",

  // Store Summary
  STORE_SUMMARY_READ = "store-summary:read",
  STORE_SUMMARY_CREATE = "store-summary:create",

  // Inputs
  INPUTS_READ = "inputs:read",
  INPUTS_UPDATE = "inputs:update",

  // Projects
  PROJECTS_READ = "projects:read",
  PROJECTS_CREATE = "projects:create",
  PROJECTS_UPDATE = "projects:update",

  // Merchant Settings
  MERCHANT_SETTINGS_READ = "merchant-settings:read",
  MERCHANT_SETTINGS_UPDATE = "merchant-settings:update",

  // ETL Import
  IMPORT_ETL_CREATE = "import-etl:create",

  // Violations
  VIOLATION_READ = "violation:read",
  VIOLATION_CREATE = "violation:create",

  // Fixtures Guessing
  FIXTURES_GUESSING_READ = "fixtures-guessing:read",
  FIXTURES_GUESSING_CREATE = "fixtures-guessing:create",

  // POG Analysis
  POG_ANALYSIS_READ = "pog-analysis:read",

  // Users
  USERS_READ = "users:read",
  USERS_CREATE = "users:create",
  USERS_UPDATE = "users:update",
  USERS_DELETE = "users:delete",

  // Auth
  AUTH_CHANGE_OWN_PASSWORD = "auth:change-own-password",

  // Admin
  ADMIN_MANAGE = "admin:manage",

  // Audit
  AUDIT_READ = "audit:read",

  // Analytics
  ANALYTICS_READ = "analytics:read",

  // Simulations
  SIMULATION_READ = "simulation:read",
  SIMULATION_CREATE = "simulation:create",
  SIMULATION_UPDATE = "simulation:update",

  // Imagine
  IMAGINE_READ = "imagine:read",

  // Products
  PRODUCTS_READ = "products:read",

  // Fixtures
  FIXTURES_READ = "fixtures:read",

  // CDT (Category Decision Tree)
  CDT_RESET = "cdt:reset",
  CDT_VIEW = "cdt:view",
  CDT_EDIT = "cdt:edit",
}

const MINOR_REVIEWER_PERMISSIONS: PermissionCode[] = [
  PermissionCode.STORES_READ,
  PermissionCode.CATEGORIES_READ,
  PermissionCode.MERCHFLOW_READ,
  PermissionCode.PLANOGRAM_VIEW,
  PermissionCode.MERCHFLOW_UPDATE,
  PermissionCode.STORE_POG_DELETE,
  PermissionCode.REPORT_READ,
  PermissionCode.INPUTS_READ,
  PermissionCode.MERCHANT_SETTINGS_READ,
  PermissionCode.VIOLATION_READ,
  PermissionCode.FIXTURES_GUESSING_READ,
  PermissionCode.POG_ANALYSIS_READ,
  PermissionCode.AUTH_CHANGE_OWN_PASSWORD,
  PermissionCode.ANALYTICS_READ,
  PermissionCode.SIMULATION_READ,
  PermissionCode.SIMULATION_CREATE,
  PermissionCode.SIMULATION_UPDATE,
  PermissionCode.CDT_VIEW,
];

// Role-to-permissions mapping (code-driven, no DB tables)
export const ROLE_PERMISSIONS: Record<RoleCode, PermissionCode[]> = {
  [RoleCode.ADMIN]: [...Object.values(PermissionCode)],
  [RoleCode.OPERATOR]: [
    PermissionCode.STORES_READ,
    PermissionCode.STORES_CREATE,
    PermissionCode.STORES_UPDATE,
    PermissionCode.CATEGORIES_READ,
    PermissionCode.CATEGORIES_CREATE,
    PermissionCode.CATEGORIES_UPDATE,
    PermissionCode.MERCHFLOW_READ,
    PermissionCode.MERCHFLOW_CREATE,
    PermissionCode.MERCHFLOW_UPDATE,
    PermissionCode.MERCHFLOW_DELETE,
    PermissionCode.STORE_POG_DELETE,
    PermissionCode.PLANOGRAM_VIEW,
    PermissionCode.REPORT_READ,
    PermissionCode.STORE_SUMMARY_READ,
    PermissionCode.STORE_SUMMARY_CREATE,
    PermissionCode.INPUTS_READ,
    PermissionCode.INPUTS_UPDATE,
    PermissionCode.PROJECTS_READ,
    PermissionCode.PROJECTS_CREATE,
    PermissionCode.PROJECTS_UPDATE,
    PermissionCode.MERCHANT_SETTINGS_READ,
    PermissionCode.MERCHANT_SETTINGS_UPDATE,
    PermissionCode.IMPORT_ETL_CREATE,
    PermissionCode.VIOLATION_READ,
    PermissionCode.VIOLATION_CREATE,
    PermissionCode.FIXTURES_GUESSING_READ,
    PermissionCode.FIXTURES_GUESSING_CREATE,
    PermissionCode.POG_ANALYSIS_READ,
    PermissionCode.USERS_READ,
    PermissionCode.AUTH_CHANGE_OWN_PASSWORD,
    PermissionCode.AUDIT_READ,
    PermissionCode.ANALYTICS_READ,
    PermissionCode.SIMULATION_READ,
    PermissionCode.SIMULATION_CREATE,
    PermissionCode.SIMULATION_UPDATE,
    PermissionCode.IMAGINE_READ,
    PermissionCode.PRODUCTS_READ,
    PermissionCode.FIXTURES_READ,
    PermissionCode.CDT_RESET,
    PermissionCode.CDT_VIEW,
    PermissionCode.CDT_EDIT,
  ],

  [RoleCode.DEVELOPER]: [
    PermissionCode.STORES_READ,
    PermissionCode.STORES_CREATE,
    PermissionCode.STORES_UPDATE,
    PermissionCode.CATEGORIES_READ,
    PermissionCode.CATEGORIES_CREATE,
    PermissionCode.CATEGORIES_UPDATE,
    PermissionCode.MERCHFLOW_READ,
    PermissionCode.MERCHFLOW_CREATE,
    PermissionCode.MERCHFLOW_UPDATE,
    PermissionCode.STORE_POG_DELETE,
    PermissionCode.PLANOGRAM_VIEW,
    PermissionCode.REPORT_READ,
    PermissionCode.STORE_SUMMARY_READ,
    PermissionCode.STORE_SUMMARY_CREATE,
    PermissionCode.INPUTS_READ,
    PermissionCode.PROJECTS_READ,
    PermissionCode.PROJECTS_CREATE,
    PermissionCode.PROJECTS_UPDATE,
    PermissionCode.MERCHANT_SETTINGS_READ,
    PermissionCode.MERCHANT_SETTINGS_UPDATE,
    PermissionCode.VIOLATION_READ,
    PermissionCode.VIOLATION_CREATE,
    PermissionCode.FIXTURES_GUESSING_READ,
    PermissionCode.FIXTURES_GUESSING_CREATE,
    PermissionCode.POG_ANALYSIS_READ,
    PermissionCode.AUTH_CHANGE_OWN_PASSWORD,
    PermissionCode.ANALYTICS_READ,
    PermissionCode.SIMULATION_READ,
    PermissionCode.SIMULATION_CREATE,
    PermissionCode.SIMULATION_UPDATE,
    PermissionCode.IMAGINE_READ,
    PermissionCode.PRODUCTS_READ,
    PermissionCode.FIXTURES_READ,
    PermissionCode.CDT_RESET,
    PermissionCode.CDT_VIEW,
    PermissionCode.CDT_EDIT,
  ],

  [RoleCode.MINOR_REVIEWER]: [...MINOR_REVIEWER_PERMISSIONS],

  [RoleCode.MAJOR_REVIEWER]: [
    ...MINOR_REVIEWER_PERMISSIONS,
    PermissionCode.MERCHFLOW_CREATE,
    PermissionCode.MERCHFLOW_DELETE,
  ],

  [RoleCode.STORE_VIEWER]: [
    PermissionCode.PLANOGRAM_VIEW,
    PermissionCode.MERCHANT_SETTINGS_READ,
    PermissionCode.AUTH_CHANGE_OWN_PASSWORD,
  ],
};

// Helper functions for permission checks
export const hasPermission = (roleCode: RoleCode, permission: PermissionCode): boolean => {
  return ROLE_PERMISSIONS[roleCode].includes(permission);
};

export const hasAnyPermission = (roleCode: RoleCode, permissions: PermissionCode[]): boolean => {
  const rolePerms = ROLE_PERMISSIONS[roleCode];
  return permissions.some((p) => rolePerms.includes(p));
};

export const hasAllPermissions = (roleCode: RoleCode, permissions: PermissionCode[]): boolean => {
  const rolePerms = ROLE_PERMISSIONS[roleCode];
  return permissions.every((p) => rolePerms.includes(p));
};

/**
 * A role that may look at planograms but not at merchflows. The backend narrows its planogram
 * lists and reads to delivered store POGs; the app shows it the read-only planogram pages only.
 */
export const isDeliveredPlanogramViewerOnly = (roleCode: RoleCode): boolean =>
  hasPermission(roleCode, PermissionCode.PLANOGRAM_VIEW) &&
  !hasPermission(roleCode, PermissionCode.MERCHFLOW_READ);
